import { createFileRoute } from '@tanstack/react-router'
import { useQuery } from '@tanstack/react-query'
import { Download } from 'lucide-react'
import {
  isOpenAttestDeviceSlaExpired,
  type EmployeeDeviceDTO,
} from '@deepguide-ai/types'
import { DevicesSection } from '@/components/compliance/people/DevicesSection'
import { useEmployeeCenterDashboard } from '@/components/employee-center/EmployeeCenterEmployeeShell'
import { Badge } from '@deepguide-ai/ui/badge'
import { Button } from '@deepguide-ai/ui/button'
import { trpc } from '@/lib/trpc'

export const Route = createFileRoute(
  '/(protected)/workspaces/$workspaceId/employee-center/devices',
)({
  component: RouteComponent,
})

function RouteComponent() {
  const { dashboard } = useEmployeeCenterDashboard()
  const openAttestDevices = dashboard.devices.filter(
    (device) => device.mdmProvider === 'open-attest',
  )

  const statusQuery = useQuery(trpc.integrations.openAttestGetStatus.queryOptions())

  return (
    <div className='space-y-4'>
      <OpenAttestInstallStatus
        enabled={
          statusQuery.data?.connected === true || openAttestDevices.length > 0
        }
        devices={openAttestDevices}
      />
      <DevicesSection
        devices={dashboard.devices}
        displayStyle='table'
        allowEvidenceUpload
      />
    </div>
  )
}

function OpenAttestInstallStatus({
  enabled,
  devices,
}: {
  enabled: boolean
  devices: EmployeeDeviceDTO[]
}) {
  if (!enabled) {
    return null
  }

  const installed = devices.length > 0
  const lastSeenAt = devices
    .map((device) => device.lastCheckIn)
    .filter(
      (value): value is NonNullable<EmployeeDeviceDTO['lastCheckIn']> =>
        Boolean(value)
    )
    .sort((a, b) => new Date(b).getTime() - new Date(a).getTime())[0]
  const hasExpiredNonCompliance = devices.some(
    (device) =>
      device.complianceStatus === 'NON_COMPLIANT' &&
      isOpenAttestDeviceSlaExpired(device),
  )
  const hasNonCompliantDevice = devices.some(
    (device) => device.complianceStatus === 'NON_COMPLIANT',
  )

  const status = !installed
    ? {
        label: 'Not installed',
        badgeStatus: 'amber' as const,
        message:
          'No Open-Attest device has checked in for your employee profile yet.',
      }
    : hasExpiredNonCompliance
      ? {
          label: 'Non-compliant',
          badgeStatus: 'red' as const,
          message:
            'Open-Attest has not collected device posture within the 24-hour SLA.',
        }
      : hasNonCompliantDevice
        ? {
            label: 'Non-compliant',
            badgeStatus: 'red' as const,
            message:
              'Open-Attest is installed, but one or more required device checks need attention.',
          }
        : {
            label: 'Installed',
            badgeStatus: 'green' as const,
            message:
              'Open-Attest is installed and sending current device posture.',
          }

  return (
    <div className='flex flex-wrap items-center justify-between gap-2'>
      <div className='flex flex-wrap items-center gap-2 text-[12px] text-text-subtle'>
        <span className='font-medium text-text-secondary'>Open-Attest agent</span>
        <Badge type='dot' status={status.badgeStatus} title={status.message}>
          {status.label}
        </Badge>
        {lastSeenAt && (
          <span>Last check-in: {new Date(lastSeenAt).toLocaleString()}</span>
        )}
      </div>

      {!installed && (
        <Button
          variant='outline'
          size='sm'
          className='h-8 shrink-0 rounded-sm text-[12px]'
          onClick={() =>
            window.open(
              'https://github.com/Screenata/open-attest',
              '_blank',
              'noopener,noreferrer',
            )
          }
        >
          <Download className='h-3.5 w-3.5' />
          Install agent
        </Button>
      )}
    </div>
  )
}
